import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { Tabs } from '../../components/Common';
import CatalogueServices from './CatalogueServices';
import CatalogueProfiles from './CatalogueProfiles';
import CatalogueReviews from './CatalogueReviews';
import CatalogueQuestions from './CatalogueQuestions';

const Catalogue: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as 'services' | 'profiles' | 'reviews' | 'questions' | null;
  const [activeTab, setActiveTab] = useState<'services' | 'profiles' | 'reviews' | 'questions'>(
    tabFromUrl && ['services', 'profiles', 'reviews', 'questions'].includes(tabFromUrl) 
      ? tabFromUrl 
      : 'services'
  );
  
  // Refs to access child component methods
  const servicesRef = useRef<any>(null);
  const profilesRef = useRef<any>(null);
  const reviewsRef = useRef<any>(null);
  const questionsRef = useRef<any>(null);
  
  const [bulkApproving, setBulkApproving] = useState(false);
  const [itemCount, setItemCount] = useState(0);

  // Update URL when tab changes
  const handleTabChange = (tabKey: string) => {
    const newTab = tabKey as typeof activeTab;
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  // Sync activeTab with URL on mount and when URL changes
  useEffect(() => {
    const urlTab = searchParams.get('tab') as typeof activeTab | null;
    if (urlTab && ['services', 'profiles', 'reviews', 'questions'].includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [searchParams]);

  const tabs = [
    { key: 'services', label: 'Services'},
    { key: 'profiles', label: 'Profiles'},
    { key: 'reviews', label: 'Reviews' },
    { key: 'questions', label: 'Questions' },
  ];

  // Handle bulk approve based on active tab
  const handleBulkApprove = async () => {
    setBulkApproving(true);
    
    try {
      switch (activeTab) {
        case 'services':
          if (servicesRef.current?.handleApproveAll) {
            await servicesRef.current.handleApproveAll();
          }
          break;
        case 'profiles':
          if (profilesRef.current?.handleApproveAll) {
            await profilesRef.current.handleApproveAll();
          }
          break;
        case 'reviews':
          if (reviewsRef.current?.handleBulkApprove) {
            await reviewsRef.current.handleBulkApprove();
          }
          break;
        case 'questions':
          if (questionsRef.current?.handleBulkApprove) {
            await questionsRef.current.handleBulkApprove();
          }
          break;
      }
    } finally {
      setBulkApproving(false);
    }
  };

  return (
    <Box
      className="page-container"
      sx={{
        padding: {
          xs: '24px 16px',
          sm: '40px 40px',
          md: '40px 80px',
        },
        gap: 'var(--palette-light-6-value)', // 24px
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--palette-light-4-value)', // 16px
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: 'var(--palette-light-font-size-h1-value)', // 44px
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: '-1%',
            color: 'var(--palette-light-text-primary-value-hex)',
          }}
        >
          Content catalogue
        </Typography>

        {/* Approve All Button */}
        {itemCount > 0 && (
          <Button
            variant="contained"
            onClick={handleBulkApprove}
            disabled={bulkApproving}
            sx={{
              height: 'var(--palette-light-14-value)',
              padding: '0 var(--palette-light-8-value)',
              borderRadius: 'var(--palette-light-4-value)',
              backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
              color: 'var(--palette-light-text-foreground-value-hex)',
              fontSize: 'var(--palette-light-font-size-default-value)',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
                opacity: 0.9,
              },
              '&:disabled': {
                backgroundColor: 'var(--palette-light-text-tertiary-value-hex)',
                color: 'var(--palette-light-text-foreground-value-hex)',
              },
            }}
          >
            {bulkApproving ? <CircularProgress size={20} color="inherit" /> : `Approve All (${itemCount})`}
          </Button>
        )}
      </Box>

      {/* Tabs and Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-4-value)', flex: 1, overflow: 'hidden' }}>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />

        {/* Content based on active tab */}
        {activeTab === 'services' && <CatalogueServices ref={servicesRef} onItemCountChange={setItemCount} />}
        {activeTab === 'profiles' && <CatalogueProfiles ref={profilesRef} onItemCountChange={setItemCount} />}
        {activeTab === 'reviews' && <CatalogueReviews ref={reviewsRef} onItemCountChange={setItemCount} />}
        {activeTab === 'questions' && <CatalogueQuestions ref={questionsRef} onItemCountChange={setItemCount} />}
      </Box>
    </Box>
  );
};

export default Catalogue;
